import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Link2, Plus, Trash2, Loader2, Copy, Upload, Zap, ShieldCheck, Clock,
  CheckCircle2, Lock, Circle, RefreshCw, AlertTriangle, ListVideo, Pencil, Eye,
  Smartphone, ShieldOff, RotateCcw,
} from "lucide-react";
import { format } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { copyToClipboard } from "@/lib/clipboard";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
  available: { cls: "bg-success/15 text-success border-success/30", icon: Circle, label: "Available" },
  reserved: { cls: "bg-warning/15 text-warning border-warning/30", icon: Lock, label: "Reserved" },
  active: { cls: "bg-primary/15 text-primary border-primary/30", icon: CheckCircle2, label: "Active" },
  expired: { cls: "bg-destructive/15 text-destructive border-destructive/30", icon: Clock, label: "Expired" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.available;
  return <Badge variant="outline" className={`gap-1.5 capitalize ${m.cls}`}><m.icon className="h-3 w-3" /> {m.label}</Badge>;
}

function StatCard({ label, value, tone, testid }) {
  return (
    <Card className="p-4 border-border bg-card" data-testid={testid}>
      <p className="text-3xl font-display font-bold tabular-nums">{value}</p>
      <p className={`text-xs uppercase tracking-[0.15em] mt-1 ${tone}`}>{label}</p>
    </Card>
  );
}

export default function LinkPool() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [links, setLinks] = useState(null);
  const [stats, setStats] = useState(null);
  const [panels, setPanels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("all");

  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanningId, setScanningId] = useState(null);
  const [secureResult, setSecureResult] = useState(null);
  const [sourceFor, setSourceFor] = useState(null);
  const [sourceForm, setSourceForm] = useState({});
  const [revealed, setRevealed] = useState({});
  const [deviceFor, setDeviceFor] = useState(null);
  const [deviceBusy, setDeviceBusy] = useState(false);

  const [imp, setImp] = useState({ panel_id: "", template_id: "", link_type: "m3u", name: "", url: "", server_url: "", username: "", password: "", device_lock: false, device_lock_allow_app_update: true });
  const [bulk, setBulk] = useState({ panel_id: "", template_id: "", raw: "" });
  const [test, setTest] = useState({ full_name: "", username: "", password: "", panel_id: "", template_id: "", duration_hours: null });
  const [durations, setDurations] = useState([]);

  const load = async () => {
    try {
      const [l, s] = await Promise.all([api.get(`/links${tab !== "all" ? `?status=${tab}` : ""}`), api.get("/links/stats")]);
      setLinks(l.data); setStats(s.data);
    } catch { toast.error("Failed to load link pool"); }
  };
  useEffect(() => { load(); }, [tab]);
  useEffect(() => {
    api.get("/panels").then(({ data }) => setPanels(data)).catch(() => {});
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  const templatesFor = (panelId) => templates.filter((t) => !t.panel_id || t.panel_id === panelId);

  // fetch allowed durations when panel changes in test dialog
  useEffect(() => {
    if (test.panel_id) {
      api.get(`/tests/allowed-durations?panel_id=${test.panel_id}`).then(({ data }) => {
        setDurations(data.durations);
        setTest((t) => ({ ...t, duration_hours: data.durations[0] || null }));
      }).catch(() => setDurations([]));
    }
  }, [test.panel_id]);

  const doImport = async () => {
    setBusy(true);
    try {
      const payload = { panel_id: imp.panel_id, template_id: imp.template_id, link_type: imp.link_type, name: imp.name, device_lock: imp.device_lock, device_lock_allow_app_update: imp.device_lock_allow_app_update };
      if (imp.link_type === "m3u") payload.url = imp.url;
      else { payload.server_url = imp.server_url; payload.username = imp.username; payload.password = imp.password; }
      const { data } = await api.post("/links", payload);
      if (data.scan) {
        const b = data.scan.breakdown || {};
        toast.success(`Imported — ${data.scan.entries} channels (${b.live || 0} live, ${b.movie || 0} movies, ${b.series || 0} series), ${data.scan.categories_added} new categories`);
      } else if (data.scan_error) {
        toast.warning(`Link added but playlist could not be parsed: ${data.scan_error}`);
      } else {
        toast.success("Link imported to pool");
      }
      setImportOpen(false);
      setImp({ panel_id: "", template_id: "", link_type: "m3u", name: "", url: "", server_url: "", username: "", password: "", device_lock: false, device_lock_allow_app_update: true });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const rescan = async (l) => {
    setScanningId(l.id);
    try {
      const { data } = await api.post(`/links/${l.id}/scan`);
      const b = data.breakdown || {};
      toast.success(`Scanned — ${data.entries} channels (${b.live || 0} live, ${b.movie || 0} movies, ${b.series || 0} series)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setScanningId(null); }
  };

  const doBulk = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/links/bulk", bulk);
      toast.success(`${data.imported} links imported`);
      setBulkOpen(false); setBulk({ panel_id: "", template_id: "", raw: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const doTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tests", test);
      setSecureResult(data);
      toast.success("Test account created — link reserved");
      setTestOpen(false);
      setTest({ full_name: "", username: "", password: "", panel_id: "", template_id: "", duration_hours: null });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const openSource = (l) => {
    const sf = l.source_fields || {};
    setSourceForm(sf.link_type === "xtream"
      ? { link_type: "xtream", server: sf.server || "", username: sf.username || "", password: sf.password || "" }
      : { link_type: "m3u", url: sf.url || "" });
    setSourceFor(l);
  };

  const saveSource = async () => {
    setBusy(true);
    try {
      const payload = sourceForm.link_type === "xtream"
        ? { server: sourceForm.server, username: sourceForm.username, password: sourceForm.password }
        : { url: sourceForm.url };
      await api.put(`/links/${sourceFor.id}/source`, payload);
      toast.success("Source updated & re-scanned");
      setSourceFor(null); load();
    } catch (err) {
      // Backend commits the new source BEFORE scanning, so a scan failure still saved the URL.
      toast.warning(`Source saved, but re-scan failed: ${formatApiError(err.response?.data?.detail) || err.message}`);
      setSourceFor(null); load();
    }
    finally { setBusy(false); }
  };

  const saveDeviceLock = async (patch) => {
    if (!deviceFor) return;
    setDeviceBusy(true);
    try {
      const { data } = await api.put(`/links/${deviceFor.id}/device-lock`, patch);
      setDeviceFor(data);
      setLinks((ls) => (ls || []).map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      toast.success("Device lock updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setDeviceBusy(false); }
  };

  const resetDevice = async () => {
    if (!deviceFor) return;
    setDeviceBusy(true);
    try {
      const { data } = await api.post(`/links/${deviceFor.id}/reset-device`);
      setDeviceFor(data);
      setLinks((ls) => (ls || []).map((x) => (x.id === data.id ? { ...x, ...data } : x)));
      toast.success("Device reset — the next device to connect will be locked");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setDeviceBusy(false); }
  };

  const remove = async (l) => {
    try { await api.delete(`/links/${l.id}?force=true`); toast.success("Link deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/links/all${tab !== "all" ? `?status=${tab}` : ""}`);
      toast.success(`${data.deleted} link(s) deleted`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const copy = async (v) => {
    const ok = await copyToClipboard(v);
    ok ? toast.success("Copied") : toast.error("Copy failed — select and copy manually");
  };
  const panelName = (id) => panels.find((p) => p.id === id)?.name || "—";
  const templateName = (id) => templates.find((t) => t.id === id)?.name || "—";

  return (
    <div data-testid="linkpool-page">
      <PageHeader overline="Provisioning" title="Link Pool" description="Import M3U / Xtream links. StreamCore masks the source and provisions secure customer links.">
        <div className="flex gap-2">
          <Dialog open={testOpen} onOpenChange={setTestOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="create-test-button"><Zap className="h-4 w-4 mr-2" /> Create Test</Button></DialogTrigger>
            <DialogContent className="bg-popover sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">Create Test Account</DialogTitle>
                <DialogDescription>Auto-reserves the first available link and generates a secure StreamCore URL.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>Customer name</Label><Input value={test.full_name} onChange={(e) => setTest((t) => ({ ...t, full_name: e.target.value }))} placeholder="John Doe" data-testid="test-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Username <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={test.username} onChange={(e) => setTest((t) => ({ ...t, username: e.target.value }))} placeholder="auto-generated" data-testid="test-username" /></div>
                  <div className="space-y-2"><Label>Password <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={test.password} onChange={(e) => setTest((t) => ({ ...t, password: e.target.value }))} placeholder="auto-generated" data-testid="test-password" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Panel</Label>
                    <Select value={test.panel_id} onValueChange={(v) => setTest((t) => ({ ...t, panel_id: v, template_id: "" }))}>
                      <SelectTrigger data-testid="test-panel"><SelectValue placeholder="Panel" /></SelectTrigger>
                      <SelectContent className="bg-popover">{panels.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Template</Label>
                    <Select value={test.template_id} onValueChange={(v) => setTest((t) => ({ ...t, template_id: v }))} disabled={!test.panel_id}>
                      <SelectTrigger data-testid="test-template"><SelectValue placeholder="Template" /></SelectTrigger>
                      <SelectContent className="bg-popover">{templatesFor(test.panel_id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Test duration</Label>
                  {test.panel_id && durations.length === 0 ? (
                    <p className="text-xs text-destructive">No durations allowed for you on this panel.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="test-durations">
                      {durations.map((d) => (
                        <button key={d} type="button" onClick={() => setTest((t) => ({ ...t, duration_hours: d }))} data-testid={`test-duration-${d}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${test.duration_hours === d ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                          {d >= 168 ? `${d / 24}d` : `${d}h`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={doTest} disabled={busy || !test.full_name || !test.panel_id || !test.template_id || !test.duration_hours} data-testid="test-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create & reserve link
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {isAdmin && (<>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive hover:text-destructive" disabled={busy || !links || links.length === 0} data-testid="clear-pool-button">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />} Delete All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-popover">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {tab === "all" ? "all links" : `all ${tab} links`}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes {tab === "all" ? "every link" : `every ${tab} link`} from the pool along with any customer/test accounts using them. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={clearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="clear-pool-confirm">Delete All</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="bulk-import-button"><Upload className="h-4 w-4 mr-2" /> Bulk</Button></DialogTrigger>
            <DialogContent className="bg-popover sm:max-w-md">
              <DialogHeader><DialogTitle className="font-display">Bulk import M3U links</DialogTitle>
                <DialogDescription>One M3U URL per line. All go to the selected panel & template.</DialogDescription></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Panel</Label>
                    <Select value={bulk.panel_id} onValueChange={(v) => setBulk((b) => ({ ...b, panel_id: v, template_id: "" }))}>
                      <SelectTrigger data-testid="bulk-panel"><SelectValue placeholder="Panel" /></SelectTrigger>
                      <SelectContent className="bg-popover">{panels.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Template</Label>
                    <Select value={bulk.template_id} onValueChange={(v) => setBulk((b) => ({ ...b, template_id: v }))} disabled={!bulk.panel_id}>
                      <SelectTrigger data-testid="bulk-template"><SelectValue placeholder="Template" /></SelectTrigger>
                      <SelectContent className="bg-popover">{templatesFor(bulk.panel_id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Links</Label><Textarea value={bulk.raw} onChange={(e) => setBulk((b) => ({ ...b, raw: e.target.value }))} rows={6} placeholder={"http://provider.com/get.php?username=..\nhttp://provider.com/get.php?username=.."} className="font-mono text-xs" data-testid="bulk-raw" /></div>
              </div>
              <DialogFooter>
                <Button onClick={doBulk} disabled={busy || !bulk.panel_id || !bulk.template_id || !bulk.raw.trim()} data-testid="bulk-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild><Button data-testid="import-link-button"><Plus className="h-4 w-4 mr-2" /> Add Link</Button></DialogTrigger>
            <DialogContent className="bg-popover sm:max-w-md">
              <DialogHeader><DialogTitle className="font-display">Import Link</DialogTitle>
                <DialogDescription>The original URL is encrypted and never shown to customers.</DialogDescription></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Panel</Label>
                    <Select value={imp.panel_id} onValueChange={(v) => setImp((s) => ({ ...s, panel_id: v, template_id: "" }))}>
                      <SelectTrigger data-testid="import-panel"><SelectValue placeholder="Panel" /></SelectTrigger>
                      <SelectContent className="bg-popover">{panels.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Template</Label>
                    <Select value={imp.template_id} onValueChange={(v) => setImp((s) => ({ ...s, template_id: v }))} disabled={!imp.panel_id}>
                      <SelectTrigger data-testid="import-template"><SelectValue placeholder="Template" /></SelectTrigger>
                      <SelectContent className="bg-popover">{templatesFor(imp.panel_id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Tabs value={imp.link_type} onValueChange={(v) => setImp((s) => ({ ...s, link_type: v }))}>
                  <TabsList className="w-full">
                    <TabsTrigger value="m3u" className="flex-1" data-testid="import-type-m3u">M3U URL</TabsTrigger>
                    <TabsTrigger value="xtream" className="flex-1" data-testid="import-type-xtream">Xtream</TabsTrigger>
                  </TabsList>
                </Tabs>
                {imp.link_type === "m3u" ? (
                  <div className="space-y-2"><Label>M3U URL</Label><Input value={imp.url} onChange={(e) => setImp((s) => ({ ...s, url: e.target.value }))} placeholder="http://provider.com/get.php?..." className="font-mono text-xs" data-testid="import-url" /></div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2"><Label>Server URL</Label><Input value={imp.server_url} onChange={(e) => setImp((s) => ({ ...s, server_url: e.target.value }))} placeholder="http://host:8080" className="font-mono text-xs" data-testid="import-server" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Username</Label><Input value={imp.username} onChange={(e) => setImp((s) => ({ ...s, username: e.target.value }))} className="font-mono text-xs" data-testid="import-username" /></div>
                      <div className="space-y-2"><Label>Password</Label><Input value={imp.password} onChange={(e) => setImp((s) => ({ ...s, password: e.target.value }))} className="font-mono text-xs" data-testid="import-password" /></div>
                    </div>
                  </div>
                )}
                <div className="rounded-lg border border-border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4 text-primary" />
                      <div>
                        <Label className="text-sm">Lock to First Device</Label>
                        <p className="text-[11px] text-muted-foreground">Only the first device to use this link is allowed. Prevents sharing.</p>
                      </div>
                    </div>
                    <Switch checked={imp.device_lock} onCheckedChange={(v) => setImp((s) => ({ ...s, device_lock: v }))} data-testid="import-device-lock" />
                  </div>
                  {imp.device_lock && (
                    <div className="flex items-center justify-between border-t border-border pt-3">
                      <div>
                        <Label className="text-sm">Allow same device after app update</Label>
                        <p className="text-[11px] text-muted-foreground">Ignore User-Agent changes when the MAC / Device ID stays the same.</p>
                      </div>
                      <Switch checked={imp.device_lock_allow_app_update} onCheckedChange={(v) => setImp((s) => ({ ...s, device_lock_allow_app_update: v }))} data-testid="import-allow-app-update" />
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={doImport} disabled={busy || !imp.panel_id || !imp.template_id || (imp.link_type === "m3u" ? !imp.url : !(imp.server_url && imp.username && imp.password))} data-testid="import-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>)}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Available" value={stats?.available ?? "—"} tone="text-success" testid="stat-available" />
        <StatCard label="Reserved" value={stats?.reserved ?? "—"} tone="text-warning" testid="stat-reserved" />
        <StatCard label="Active" value={stats?.active ?? "—"} tone="text-primary" testid="stat-active" />
        <StatCard label="Expiring 3d" value={stats?.expiring ?? "—"} tone="text-warning" testid="stat-expiring" />
        <StatCard label="Expired" value={stats?.expired ?? "—"} tone="text-destructive" testid="stat-expired" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="h-auto">
          <TabsTrigger value="all" data-testid="pool-tab-all">All</TabsTrigger>
          <TabsTrigger value="available" data-testid="pool-tab-available">Available</TabsTrigger>
          <TabsTrigger value="reserved" data-testid="pool-tab-reserved">Reserved</TabsTrigger>
          <TabsTrigger value="active" data-testid="pool-tab-active">Active</TabsTrigger>
          <TabsTrigger value="expired" data-testid="pool-tab-expired">Expired</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="border-border overflow-hidden">
        {!links ? (
          <div className="p-4 space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : links.length === 0 ? (
          <div className="p-12 text-center">
            <Link2 className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">{isAdmin ? "No links here. Import your first link to build the pool." : "No links assigned yet. Create a test account and a link will be auto-assigned from the pool."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Source (masked)</TableHead>
                  <TableHead>Panel</TableHead>
                  <TableHead className="hidden md:table-cell">Template</TableHead>
                  <TableHead className="hidden sm:table-cell">Channels</TableHead>
                  <TableHead>Secure Link</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((l) => (
                  <TableRow key={l.id} data-testid={`link-row-${l.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-1.5 font-mono text-xs">
                        {isAdmin && l.original_source ? (
                          revealed[l.id] ? (
                            <button onClick={() => copy(l.original_source)} className="flex items-center gap-1.5 text-accent hover:underline max-w-[220px] truncate" title="Click to copy" data-testid={`link-source-real-${l.id}`}>
                              <Eye className="h-3 w-3 shrink-0" /> <span className="truncate">{l.original_source}</span> <Copy className="h-3 w-3 shrink-0" />
                            </button>
                          ) : (
                            <button onClick={() => setRevealed((r) => ({ ...r, [l.id]: true }))} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground" data-testid={`link-source-reveal-${l.id}`}>
                              <Lock className="h-3 w-3" /> {l.masked_source} <span className="text-[10px] underline">reveal</span>
                            </button>
                          )
                        ) : (
                          <><Lock className="h-3 w-3 text-muted-foreground" /> {l.masked_source}</>
                        )}
                        <Badge variant="outline" className="text-[10px] uppercase">{l.link_type}</Badge>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="secondary">{panelName(l.panel_id)}</Badge></TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{templateName(l.template_id)}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {l.parse_status === "ok" ? (
                        <span className="flex items-center gap-1 text-xs text-success font-mono"><ListVideo className="h-3.5 w-3.5" /> {l.entry_count}</span>
                      ) : l.parse_status === "failed" ? (
                        <span className="flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> failed</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">pending</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {l.secure_url ? (
                        <div className="flex flex-col gap-1">
                          <button onClick={() => copy(l.secure_url)} className="flex items-center gap-1.5 text-xs text-accent hover:underline font-mono max-w-[180px] truncate" data-testid={`link-secure-${l.id}`}>
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{l.secure_url.replace(/^https?:\/\//, "")}</span> <Copy className="h-3 w-3 shrink-0" />
                          </button>
                          {l.device_lock && (
                            <Badge variant="outline" className={`w-fit text-[9px] gap-1 ${l.locked_device ? "border-primary/40 text-primary" : "border-warning/40 text-warning"}`} data-testid={`link-lock-badge-${l.id}`}>
                              <Smartphone className="h-2.5 w-2.5" /> {l.locked_device ? "Locked" : "Awaiting device"}
                            </Badge>
                          )}
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground font-mono">
                      {l.expires_at ? (
                        <span className={l.expiring_soon ? "text-warning font-medium" : ""}>{format(new Date(l.expires_at), "dd MMM HH:mm")}{l.expiring_soon && <Badge variant="outline" className="ml-1.5 text-[9px] border-warning/40 text-warning">soon</Badge>}</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openSource(l)} title="Edit original source" data-testid={`link-edit-source-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                        )}
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className={`h-8 w-8 ${l.device_lock ? "text-primary" : ""}`} onClick={() => setDeviceFor(l)} title="Device lock" data-testid={`link-device-lock-${l.id}`}>
                            {l.device_lock ? <Smartphone className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rescan(l)} disabled={scanningId === l.id} title="Re-scan playlist" data-testid={`link-scan-${l.id}`}>
                          {scanningId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`link-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-popover">
                            <AlertDialogHeader><AlertDialogTitle>Delete link?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This removes the link from the pool.
                                {(l.status === "reserved" || l.status === "active") && " This link is currently in use — its customer/test account will also be removed."}
                              </AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(l)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Edit original source (admin) */}
      <Dialog open={!!sourceFor} onOpenChange={(o) => !o && setSourceFor(null)}>
        <DialogContent className="bg-popover sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Edit original source</DialogTitle>
            <DialogDescription>Update the hidden provider URL/credentials. The link is re-scanned after saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {sourceForm.link_type === "xtream" ? (
              <>
                <div className="space-y-2"><Label>Server URL</Label><Input value={sourceForm.server || ""} onChange={(e) => setSourceForm((s) => ({ ...s, server: e.target.value }))} className="font-mono text-xs" data-testid="source-server" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Username</Label><Input value={sourceForm.username || ""} onChange={(e) => setSourceForm((s) => ({ ...s, username: e.target.value }))} className="font-mono text-xs" data-testid="source-username" /></div>
                  <div className="space-y-2"><Label>Password</Label><Input value={sourceForm.password || ""} onChange={(e) => setSourceForm((s) => ({ ...s, password: e.target.value }))} className="font-mono text-xs" data-testid="source-password" /></div>
                </div>
              </>
            ) : (
              <div className="space-y-2"><Label>M3U URL</Label><Input value={sourceForm.url || ""} onChange={(e) => setSourceForm((s) => ({ ...s, url: e.target.value }))} className="font-mono text-xs" data-testid="source-url" /></div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveSource} disabled={busy} data-testid="source-save">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save & re-scan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device lock manager (admin) */}
      <Dialog open={!!deviceFor} onOpenChange={(o) => !o && setDeviceFor(null)}>
        <DialogContent className="bg-popover sm:max-w-md" data-testid="device-lock-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" /> Device Lock</DialogTitle>
            <DialogDescription>Allow only one device per link. Prevents customers from sharing their subscription.</DialogDescription>
          </DialogHeader>
          {deviceFor && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Lock to First Device</Label>
                  <Switch checked={!!deviceFor.device_lock} disabled={deviceBusy} onCheckedChange={(v) => saveDeviceLock({ device_lock: v })} data-testid="device-lock-toggle" />
                </div>
                {deviceFor.device_lock && (
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <div>
                      <Label className="text-sm">Allow same device after app update</Label>
                      <p className="text-[11px] text-muted-foreground">Ignore User-Agent changes if MAC / Device ID matches.</p>
                    </div>
                    <Switch checked={!!deviceFor.device_lock_allow_app_update} disabled={deviceBusy} onCheckedChange={(v) => saveDeviceLock({ device_lock_allow_app_update: v })} data-testid="device-allow-app-update-toggle" />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Status:</span>
                {!deviceFor.device_lock ? (
                  <Badge variant="outline" className="gap-1"><ShieldOff className="h-3 w-3" /> Disabled</Badge>
                ) : deviceFor.locked_device ? (
                  <Badge variant="outline" className="gap-1 border-primary/40 text-primary" data-testid="device-status-locked"><Lock className="h-3 w-3" /> Locked</Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 border-warning/40 text-warning" data-testid="device-status-awaiting"><Clock className="h-3 w-3" /> Awaiting first device</Badge>
                )}
              </div>

              {deviceFor.device_lock && deviceFor.locked_device && (
                <div className="rounded-md border border-border bg-secondary/40 p-3 space-y-1.5 text-xs" data-testid="locked-device-info">
                  <p className="flex justify-between gap-3"><span className="text-muted-foreground">Device Name</span><span className="font-mono truncate max-w-[220px]">{deviceFor.locked_device.name || "—"}</span></p>
                  <p className="flex justify-between gap-3"><span className="text-muted-foreground">MAC</span><span className="font-mono">{deviceFor.locked_device.mac || "—"}</span></p>
                  <p className="flex justify-between gap-3"><span className="text-muted-foreground">Device Key / ID</span><span className="font-mono truncate max-w-[220px]">{deviceFor.locked_device.device_id || "—"}</span></p>
                  <p className="flex justify-between gap-3"><span className="text-muted-foreground">User-Agent</span><span className="font-mono truncate max-w-[220px]" title={deviceFor.locked_device.ua}>{deviceFor.locked_device.ua || "—"}</span></p>
                  <p className="flex justify-between gap-3"><span className="text-muted-foreground">IP</span><span className="font-mono">{deviceFor.locked_device.last_ip || deviceFor.locked_device.first_ip || "—"}</span></p>
                  <p className="flex justify-between gap-3"><span className="text-muted-foreground">First Login</span><span className="font-mono">{deviceFor.locked_device.first_login ? format(new Date(deviceFor.locked_device.first_login), "dd MMM yyyy HH:mm") : "—"}</span></p>
                  <p className="flex justify-between gap-3"><span className="text-muted-foreground">Last Activity</span><span className="font-mono">{deviceFor.locked_device.last_activity ? format(new Date(deviceFor.locked_device.last_activity), "dd MMM yyyy HH:mm") : "—"}</span></p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {deviceFor?.locked_device && (
              <Button variant="outline" onClick={resetDevice} disabled={deviceBusy} className="text-warning hover:text-warning" data-testid="reset-device-button">
                {deviceBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />} Reset Device
              </Button>
            )}
            <Button variant="secondary" onClick={() => setDeviceFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secure link result */}
      <Dialog open={!!secureResult} onOpenChange={(o) => !o && setSecureResult(null)}>
        <DialogContent className="bg-popover sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-success" /> Test account ready</DialogTitle>
            <DialogDescription>Share this StreamCore URL with the customer. The original provider URL stays hidden.</DialogDescription>
          </DialogHeader>
          {secureResult && (
            <div className="space-y-3 py-2">
              <div className="rounded-md border border-border bg-secondary/40 p-3 space-y-1.5 text-sm">
                <p className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{secureResult.full_name}</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">Username</span><span className="font-mono">{secureResult.username}</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">Password</span><span className="font-mono">{secureResult.password}</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">Expires</span><span className="font-mono">{secureResult.expiry_date ? format(new Date(secureResult.expiry_date), "dd MMM HH:mm") : "—"}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Universal M3U link <span className="text-muted-foreground font-normal">(IBO Pro Player, SmartOne, Hot Player, Smarters)</span></Label>
                <div className="flex gap-2">
                  <Input readOnly value={secureResult.m3u_url || secureResult.secure_url} className="font-mono text-xs" data-testid="secure-url-output" />
                  <Button size="icon" onClick={() => copy(secureResult.m3u_url || secureResult.secure_url)} data-testid="copy-secure-url"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              {secureResult.portal_m3u && (
                <div className="space-y-2 rounded-md border border-warning/50 bg-warning/5 p-3">
                  <Label className="text-warning">IBO Player (classic) — use THIS short link</Label>
                  <p className="text-[11px] text-muted-foreground leading-snug">The classic IBO Player only works with this short link. Choose "Add Playlist" (not XC).</p>
                  <div className="flex gap-2">
                    <Input readOnly value={secureResult.portal_m3u} className="font-mono text-xs" data-testid="secure-portal-m3u" />
                    <Button size="icon" onClick={() => copy(secureResult.portal_m3u)} data-testid="copy-portal-m3u"><Copy className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
              {secureResult.xtream && (
                <div className="space-y-2 rounded-md border border-accent/30 bg-accent/5 p-3" data-testid="xtream-block">
                  <Label className="text-accent">Xtream Login <span className="text-muted-foreground font-normal">(Hot Player & Xtream apps)</span></Label>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-16 shrink-0">Server</span><Input readOnly value={secureResult.xtream.server_url} className="font-mono text-xs" data-testid="xtream-server" /><Button size="icon" variant="outline" onClick={() => copy(secureResult.xtream.server_url)}><Copy className="h-4 w-4" /></Button></div>
                    <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-16 shrink-0">Username</span><Input readOnly value={secureResult.xtream.username} className="font-mono text-xs" data-testid="xtream-username" /><Button size="icon" variant="outline" onClick={() => copy(secureResult.xtream.username)}><Copy className="h-4 w-4" /></Button></div>
                    <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground w-16 shrink-0">Password</span><Input readOnly value={secureResult.xtream.password} className="font-mono text-xs" data-testid="xtream-password" /><Button size="icon" variant="outline" onClick={() => copy(secureResult.xtream.password)}><Copy className="h-4 w-4" /></Button></div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSecureResult(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
